// Kennisbank artikelen, gebaseerd op de evidence-set (SCP 2026, D'hert 2026,
// Cockx 2026, Divosa Benchmark 2025, VNG 2026, Ansari 2026, UWV 2026, SZW 2026).
// APA7 citaties. Elk artikel heeft een icoon (Lucide-key) en 3 key takeaways.

export const artikelen = [
  {
    slug: "digitale-werkplek-inclusie",
    thema: "Methodiek",
    icon: "accessibility",
    leesduur: "6 min",
    datum: "26 aug 2026",
    titel: "Digitale technologie is niet vanzelf inclusief",
    subtitel:
      "Realist review van 29 studies laat zien wanneer digitale werkplek-tools uitsluiten in plaats van insluiten.",
    intro:
      "Digitale tools op de werkvloer worden vaak omarmd als inclusie-versneller. Een nieuwe realist review van Ansari, Ul Ain en Hassard (2026) laat zien dat dezelfde technologie de ene werknemer met een beperking activeert en de ander uitsluit. Wat verklaart dat verschil, en wat betekent het voor gemeentelijke re-integratie?",
    takeaways: [
      "Dezelfde tool kan insluiten óf uitsluiten: het ontwerp, de organisatiepraktijk én de wettelijke context bepalen samen de uitkomst.",
      "Digitale selectie- en beoordelingssystemen gaan vaak impliciet uit van 'normale' communicatie of productiviteit, waardoor mensen met een beperking worden gefilterd.",
      "Vertrouwen, autonomie, flexibiliteit en concrete ondersteuning zijn de mechanismen die inclusief werken mogelijk maken; alleen technologie inzetten is niet voldoende.",
    ],
    body: [
      {
        h: "De studie",
        p: "Ansari, Ul Ain en Hassard (2026) voerden een realist review uit: 317 bronnen gescreend, 29 studies opgenomen, aangevuld met een focusgroep van mensen met een beperking en een expertgroep. Het doel: begrijpen wat werkt, voor wie, onder welke omstandigheden en waarom, bij digitale werkplekken.",
      },
      {
        h: "Waarom eenzelfde tool anders uitpakt",
        p: "Videobellen kan iemand met een lichamelijke beperking flexibiliteit geven, en tegelijk iemand met een stemklinische aandoening juist zichtbaar 'anders' maken in vergaderingen. De uitkomst hangt af van technologisch ontwerp, individuele omstandigheden, organisatiecultuur en het beleidskader waarin de tool gebruikt wordt.",
      },
      {
        h: "Digitale assessments als valkuil",
        p: "De auteurs waarschuwen expliciet voor digitale selectie- en beoordelingssystemen die impliciet 'normale' patronen als norm nemen. Wie langzamer typt, andere pauzes neemt of een schermlezer gebruikt kan onbedoeld lager scoren, ongeacht daadwerkelijke geschiktheid.",
      },
      {
        h: "Wat dit betekent voor LevelLeven",
        p: "In plaats van 'de mens aanpassen aan het werk' bouwt LevelLeven aan het omgekeerde: het werk, de tools en de context aanpassen aan de mens. In onze werkgeverstraining maken we begeleiders bewust van digitale drempels bij onboarding, monitoring en communicatie.",
      },
      {
        h: "Praktisch voor gemeenten",
        p: "Bij plaatsingen richting werkgevers loont het om samen met de werkgever door digitale werkstromen te lopen: welk formulier, welk portaal, welke standaardmail creëert een onbedoelde drempel? Kleine aanpassingen, groot verschil in duurzame plaatsing.",
      },
    ],
    bronnen: [
      "Ansari, B., Ul Ain, Q., & Hassard, J. (2026). Digital Workplace Technologies and the Inclusion of Disabled Workers: A Realist Review and Synthesis of Evidence and Workers' Perspectives. New Technology, Work and Employment.",
    ],
  },
  {
    slug: "generieke-werkgeversvoorziening",
    thema: "Beleid",
    icon: "handshake",
    leesduur: "5 min",
    datum: "24 aug 2026",
    titel: "Waarom de generieke werkgeversvoorziening niet landde",
    subtitel:
      "Eindevaluatie UWV/SZW: goede regeling, laag bereik, wat leert dit gemeenten en re-integratiepartners?",
    intro:
      "De experimentele subsidie Generieke Werkgeversvoorziening (2023–2026) maakte bredere werkplekaanpassingen mogelijk, ook voor groepen medewerkers tegelijk. De eindevaluatie (UWV/SZW, 2026) laat zien dat de regeling wérkte in de praktijk, maar met slechts 9 toegekende aanvragen niet structureel wordt voortgezet. Waarom?",
    takeaways: [
      "Van 36 aanvragen werden er 9 toegekend, samen €406.000 voor 115 beoogde medewerkers, een fractie van wat mogelijk was.",
      "Werkgevers rapporteerden concreet werkbehoud, taakuitbreiding en meer zelfstandigheid; werknemers meer autonomie en werkplezier.",
      "De regeling strandde op geringe bekendheid, uitvoeringscomplexiteit en versnippering; niet op inhoudelijke waarde.",
    ],
    body: [
      {
        h: "De regeling",
        p: "De Generieke Werkgeversvoorziening bood subsidie voor werkplekaanpassingen die niet aan één individu gebonden waren. Denk aan een aangepaste ruimte voor een team, of hulpmiddelen die door verschillende medewerkers gebruikt konden worden. Bedoeld om inclusief werkgeverschap laagdrempeliger te maken.",
      },
      {
        h: "Wat wél werkte",
        p: "Bij de toegekende aanvragen zagen werkgevers concrete winst: medewerkers behielden hun baan, kregen meer taken en werden zelfstandiger. Werknemers voelden meer autonomie en werkplezier, precies de doelen die je bij duurzame plaatsing wilt bereiken.",
      },
      {
        h: "Wat níet werkte",
        p: "Van 36 aanvragen kwamen slechts 9 door. De hoofdredenen: werkgevers wisten niet dat de regeling bestond, de aanvraag was complex, en de regeling stond los van de andere loketten waar werkgevers al mee te maken hebben (loonkostensubsidie, no-riskpolis, jobcoach).",
      },
      {
        h: "De les voor gemeenten",
        p: "Een werkgeversaanbod dat bestaat uit veel losse regelingen die de werkgever zelf moet uitzoeken, wordt niet gebruikt. Frictie is de belangrijkste vijand van inclusie. Wat werkt: één contactpersoon die de werkgever door alle regelingen begeleidt.",
      },
      {
        h: "Waar LevelLeven aansluit",
        p: "LevelLeven zet niet alleen de deelnemer klaar, we begeleiden ook de ontvangende werkgever door het regelingenlandschap. Zo maken we duurzame plaatsing waarschijnlijker, en verlagen we de terugvalkans die anders binnen 12 maanden ontstaat.",
      },
    ],
    bronnen: [
      "UWV & Ministerie van Sociale Zaken en Werkgelegenheid. (2026). Eindevaluatie experimentele subsidieregeling Generieke Werkgeversvoorziening. Den Haag: UWV/SZW.",
    ],
  },
  {
    slug: "ontwikkelpaden-per-sector",
    thema: "Arbeidsmarkt",
    icon: "route",
    leesduur: "5 min",
    datum: "22 aug 2026",
    titel: "Erkende ontwikkelpaden per sector, en hoe u ze benut",
    subtitel:
      "SZW publiceerde het actuele overzicht van sectorale routes, plus subsidies voor scholing en praktijkleren.",
    intro:
      "Het Ministerie van SZW (2026) publiceerde het actuele overzicht van erkende sectorale ontwikkelpaden: routes waarlangs mensen via functies, scholing en praktijkleren instromen en doorgroeien binnen sectoren. Voor gemeenten en re-integratiepartners is dit de infrastructuur waarop lokale trajecten kunnen aansluiten.",
    takeaways: [
      "Sectorale ontwikkelpaden combineren functie, scholing en praktijkleren, ideaal voor deelnemers zonder direct match op vacatures.",
      "De subsidieregeling Praktijkleren staat open tot 17 september 2026 en biedt werkgevers max €2.800 per praktijk- of werkleerplaats.",
      "De 'loopbaanbegeleidende leerweg' (LBL, voorheen derde leerweg) maakt maatwerk mogelijk voor volwassenen met een leerbehoefte én werkritme.",
    ],
    body: [
      {
        h: "Wat zijn ontwikkelpaden per sector",
        p: "Ontwikkelpaden beschrijven per sector welke functies met elkaar samenhangen, welke scholing daarbij past en welke stappen zichtbaar zijn richting duurzaam werk. Ze zijn erkend door sectoren en aangesloten op subsidiabele scholing, zoals SLIM.",
      },
      {
        h: "Sociaal werk krijgt eigen overzicht",
        p: "Voor het sociaal werk is een nieuw overzicht met subsidiabele scholing verschenen (SZW, 2026). Voor gemeenten met een grote welzijnsopgave betekent dit dat instroom vanuit de bijstand naar sociaal werk beter gefinancierd kan worden.",
      },
      {
        h: "Praktijkleren tot 17 september",
        p: "De Subsidieregeling Praktijkleren is open tot 17 september 2026. Werkgevers ontvangen maximaal €2.800 per gerealiseerde praktijk- of werkleerplaats. Voor uitstromers uit LevelLeven is dit een concrete financieringsroute voor de eerste werkervaring.",
      },
      {
        h: "LBL, maatwerk voor volwassenen",
        p: "De derde leerweg heet nu 'loopbaanbegeleidende leerweg' (LBL). Dit maakt het mogelijk om scholing modulair, in werktempo, en gecombineerd met een baan aan te bieden, precies wat past bij deelnemers die uit langdurige stilstand komen.",
      },
      {
        h: "Waar LevelLeven aansluit",
        p: "LevelLeven kan fungeren als brug tussen deelnemer en deze publieke infrastructuur. Wij verzorgen de ontwikkel- en participatiefase, en dragen de deelnemer warm over aan een sectoraal ontwikkelpad met concrete financieringsmogelijkheden.",
      },
    ],
    bronnen: [
      "Ministerie van Sociale Zaken en Werkgelegenheid. (2026). Ontwikkelpaden per sector, actueel overzicht en subsidiabele scholing. Den Haag: SZW.",
    ],
  },
  {
    slug: "motivatie-paradox",
    thema: "Beleid",
    icon: "spark",
    leesduur: "6 min",
    datum: "18 aug 2026",
    titel: "De motivatie-paradox, waarom 'harder pushen' averechts werkt",
    subtitel:
      "Wat een Zweedse RCT met 200.720 werkzoekenden ons leert over re-integratie in Nederland.",
    intro:
      "Beleidsmakers grijpen vaak naar één instrument: méér motivatie, méér prikkels, méér verplichtingen. Nieuw internationaal RCT-onderzoek laat zien dat dit de zoekinspanning van werkzoekenden juist kan verminderen. De les: motivatie zonder context werkt averechts.",
    takeaways: [
      "In een RCT met 200.720 werkzoekenden verlaagden zowel ondersteunende als controlerende motivatie-interventies de zoekinspanning én de kans op werk.",
      "Het sterkste averechtse effect trad op bij autonoom-gemotiveerde deelnemers, precies de groep die men doorgaans níet 'hoeft te motiveren'.",
      "Investeer eerst in capability en opportunity (vaardigheden, gezondheid, netwerk); motivatie-instrumenten zonder fundament creëren vooral frustratie.",
    ],
    body: [
      {
        h: "De studie",
        p: "Cockx en collega's (2026) voerden een grootschalig veldexperiment uit met 200.720 werkzoekenden in Zweden. Deelnemers werden willekeurig toegewezen aan een 'ondersteunende' motivatie-conditie, een 'controlerende' conditie of een controle-groep zonder interventie.",
      },
      {
        h: "Wat er gebeurde",
        p: "Zowel de ondersteunende als de controlerende interventies verminderden gemiddeld de zoekinspanning én het vinden van werk. Bij autonoom gemotiveerde deelnemers was het effect het sterkst, precies de groep die je normaal 'niet nodig' zou hebben te motiveren.",
      },
      {
        h: "De onderliggende logica",
        p: "Zelfdeterminatietheorie (Ryan & Deci, 2000) voorspelt dit al langer: druk van buitenaf ondermijnt de intrinsieke motivatie. Wie zich al inzet, wordt door externe druk onzeker over de eigen redenen om te zoeken. Het gevolg: minder in plaats van méér actie.",
      },
      {
        h: "Wat dit betekent voor LevelLeven",
        p: "LevelLeven is bewust géén motivatieprogramma. Het is een gedrags- én contextinterventie. We werken aan capability (vaardigheden, gezondheid, structuur) en opportunity (buddy-netwerk, EVC, werkgeverspad) tegelijkertijd, anders creëer je alleen frustratie.",
      },
      {
        h: "Praktisch voor gemeenten",
        p: "Vermijd 'motivatie-controle' als eerste instrument. Investeer in fundament vóór verplichting. En meet niet alleen uitstroom, meet ook zelfregie, gezondheid en zoekintensiteit als tussenuitkomsten.",
      },
    ],
    bronnen: [
      "Cockx, B., Egebark, J., Van Hoye, G., Videnord, E., & Vikström, J. (2026). Motivating job seekers: A field experiment (Working Paper 2026/1148). Ghent University.",
      "Ryan, R. M., & Deci, E. L. (2000). Self-determination theory and the facilitation of intrinsic motivation, social development, and well-being. American Psychologist, 55(1), 68–78.",
    ],
  },
  {
    slug: "werkgeversstraf-12-maanden",
    thema: "Arbeidsmarkt",
    icon: "briefcase",
    leesduur: "5 min",
    datum: "12 aug 2026",
    titel: "De 12-maanden werkgeversstraf, en hoe je die doorbreekt",
    subtitel:
      "Meta-analyse van 67.000 fictieve sollicitaties laat zien wanneer werkloosheid zélf een negatief signaal wordt.",
    intro:
      "Werkgevers vertellen dat ze een 'gat op het cv' liever niet zien. Een nieuwe meta-analyse kwantificeert die intuïtie: na circa 12 maanden werkloosheid ontstaat een meetbaar negatief werkgeverssignaal. Wat betekent dat voor gemeentelijke re-integratie?",
    takeaways: [
      "Onder de 12 maanden werkloosheid is er nauwelijks meetbare uitsluiting; daarboven daalt de uitnodigingskans significant.",
      "Werkgevers hanteren signaaltheorie: bij twijfel gebruiken ze proxies zoals werkloosheidsduur als productiviteitsindicator.",
      "Bouw tastbaar bewijs: EVC-certificaat, gedocumenteerd participatiepad en meetbare voortgang op zelfregie doorbreken het negatieve signaal.",
    ],
    body: [
      {
        h: "De studie",
        p: "D'hert, Baert en Lippens (2026) analyseerden 28 studies en 16 veldexperimenten waarin fictieve sollicitaties (N=67.000) op echte vacatures werden getest. Dossiers waren identiek, alleen het CV-verhaal verschilde.",
      },
      {
        h: "De kantelperiode",
        p: "Onder de 12 maanden werkloosheid is er nauwelijks meetbare uitsluiting. Boven de 12 maanden daalt de uitnodigingskans significant, ongeacht kwalificaties, leeftijd of sector.",
      },
      {
        h: "Wat werkgevers 'zien'",
        p: "Werkgevers doen aan signaaltheorie: bij twijfel gebruiken ze proxies (duur werkloosheid, gaten in het CV) om aannames te doen over productiviteit en inzetbaarheid. Motivatie of enthousiasme haalt dat oordeel niet altijd in.",
      },
      {
        h: "Employer Signal Repair",
        p: "LevelLeven's ontwikkelprogramma bouwt bewust nieuwe signalen: een EVC-certificaat van EVC Centrum VIGOR, een gedocumenteerd participatiepad, meetbare voortgang op zelfregie en belastbaarheid. Geen 'gat', maar een gedocumenteerde ontwikkelfase.",
      },
      {
        h: "Aanbeveling",
        p: "Combineer trajectbegeleiding altijd met tastbaar bewijs richting werkgevers. Enkel motivatie of coaching zonder harde documentatie loopt vast op de signaaltheorie.",
      },
    ],
    bronnen: [
      "D'hert, L., Baert, S., & Lippens, L. (2026). Unemployment, inactivity, and hiring chances: A systematic review and meta-analysis. Socio-Economic Review, 24(3), 1355–1393. https://doi.org/10.1093/ser/mwag023",
    ],
  },
  {
    slug: "divosa-uitstroom-daalt",
    thema: "Cijfers",
    icon: "trending-down",
    leesduur: "4 min",
    datum: "05 aug 2026",
    titel: "Uitstroom naar werk daalt naar 7,9%, waarom bemiddeling alleen niet meer werkt",
    subtitel:
      "De Divosa Benchmark 2025 laat een duidelijke trend zien: 243 gemeenten, dezelfde bevinding.",
    intro:
      "De landelijke Divosa Benchmark Werk & Inkomen 2025 rapporteert dat de uitstroom uit de bijstand naar werk in 2025 is gedaald naar 7,9%, tegen 11,5% in 2021. Aandeel van 'werk' als uitstroomreden daalde in dezelfde periode van 41% naar 34%. Wat is er aan de hand?",
    takeaways: [
      "Uitstroom naar werk daalde van 11,5% (2021) naar 7,9% (2025), een systemische trend over 243 gemeenten.",
      "De arbeidsmarkt is krap, maar het bijstandsbestand is niet 1-op-1 matchbaar; er groeit een groep waarbij vacature-matching niet volstaat.",
      "Wat gemeenten nodig hebben is een ontwikkel- en transitiefase vóór de reguliere trajecten, niet nóg meer bemiddeling.",
    ],
    body: [
      {
        h: "De cijfers",
        p: "Dekking: 243 gemeenten (73% van Nederland), 85% van alle mensen met algemene bijstand (Divosa, BMC & Stimulansz, 2026). De trend is over drie jaar meetbaar en systemisch, geen incident.",
      },
      {
        h: "De ontwikkelkloof",
        p: "De arbeidsmarkt is krap. Vacatures zijn er. Maar het bijstandsbestand is niet één-op-één 'matchbaar'. Er is een groeiende groep waarbij de standaard vacature-matching logica niet meer volstaat: onderliggende problematiek, langdurige inactiviteit, of ontbrekende startkwalificaties.",
      },
      {
        h: "Wat gemeenten nodig hebben",
        p: "Niet 'meer bemiddeling', maar een ontwikkel- en transitiefase tussen bijstand en de reguliere trajecten. Fundament, zelfregie, structuur, participatie, vóórdat vacature-matching zin heeft.",
      },
      {
        h: "LevelLeven als antwoord",
        p: "LevelLeven vult exact die ontwikkelkloof. Twaalf weken, met start-, tussen- en eindmeting voor de gemeente. Geen vervanging van bestaande trajecten, een aanvullende fase die de doorstroom naar bestaande trajecten mogelijk maakt.",
      },
    ],
    bronnen: [
      "Divosa, BMC, & Stimulansz. (2026). Divosa Benchmark Werk & Inkomen: Jaarrapportage 2025. Divosa.",
    ],
  },
  {
    slug: "com-b-diagnose",
    thema: "Methodiek",
    icon: "brain",
    leesduur: "7 min",
    datum: "28 jul 2026",
    titel: "COM-B als diagnostische ruggengraat, capability, opportunity, motivation",
    subtitel:
      "Waarom een goede intake begint met begrijpen wát er ontbreekt, niet met wát de klant moet doen.",
    intro:
      "Het SCP (Blijleven et al., 2026) waarschuwt voor i-frame-denken: focussen op de persoon terwijl de omgeving ongewijzigd blijft. LevelLeven gebruikt daarom het COM-B model als verplichte intake-methodiek. Zo wordt zichtbaar of het probleem in capability, opportunity of motivation zit.",
    takeaways: [
      "COM-B ontleedt gedrag in Capability, Opportunity en Motivation; ontbreekt één, dan valt gedrag stil.",
      "Bij langdurige stilstand zit de bottleneck vaker in C of O (structuur, netwerk, vaardigheden) dan in M (motivatie).",
      "Per deelnemer rapporteren we welk COM-B-onderdeel is versterkt en welke barrière nog open staat, dat maakt trajecten verantwoordbaar.",
    ],
    body: [
      {
        h: "Wat is COM-B?",
        p: "Michie, Van Stralen en West (2011) beschrijven gedrag als het samenspel van drie factoren: Capability (fysieke en mentale vaardigheid), Opportunity (fysieke en sociale kansen), en Motivation (reflectieve en automatische motivatie). Ontbreekt één van de drie, dan valt gedrag stil.",
      },
      {
        h: "Waarom dit ertoe doet in de bijstand",
        p: "Beleid richt zich vaak op de M, motivatie. Maar bij langdurige stilstand zit de bottleneck vaker in C of O: geen dagritme, geen netwerk, geen taal-, digitale of gezondheidsvaardigheden. Wie 'meer motivatie' voorschrijft aan iemand die geen dagstructuur heeft, treft doel voorbij.",
      },
      {
        h: "Hoe LevelLeven het toepast",
        p: "Elke deelnemer krijgt bij intake een COM-B-scan (waar zit de barrière?). Dat bepaalt de mix: bij een C-tekort investeren we in training en gezondheid; bij een O-tekort in buddy, netwerk en werkgeverscontact; bij een M-tekort in reflectie, transactionele analyse en zelfdeterminatie.",
      },
      {
        h: "Verantwoording aan de gemeente",
        p: "In de kwartaalrapportage vertellen we per deelnemer: welke van de drie componenten is versterkt, met welke interventie, en welke barrière is nog open. Dat maakt het traject voor uw wethouder verdedigbaar én bespreekbaar.",
      },
    ],
    bronnen: [
      "Blijleven, W., Van Romondt Vis, P., Sarwary, S., & Steenbekkers, A. (2026). Gedragsinzichten voor mensgericht beleid: Lessen uit een casus over de preventie van geldzorgen. Sociaal en Cultureel Planbureau.",
      "Michie, S., Van Stralen, M. M., & West, R. (2011). The behaviour change wheel: A new method for characterising and designing behaviour change interventions. Implementation Science, 6, 42. https://doi.org/10.1186/1748-5908-6-42",
    ],
  },
  {
    slug: "vng-model-verordening",
    thema: "Beleid",
    icon: "file-text",
    leesduur: "5 min",
    datum: "20 jul 2026",
    titel: "De nieuwe VNG-modelverordening, een kans voor gemeenten",
    subtitel:
      "Wat de nieuwe Model Re-integratieverordening (juli 2026) betekent voor uw participatiebeleid.",
    intro:
      "De VNG publiceerde in juli 2026 de nieuwe Model Re-integratieverordening Participatiewet. De verordening biedt gemeenten meer ruimte voor een expliciete participatie- en ontwikkelfase, precies waar LevelLeven op is gebouwd.",
    takeaways: [
      "De nieuwe modelverordening geeft ruimte voor een expliciete ontwikkel- en participatiefase vóór directe bemiddeling naar werk.",
      "Focus verschuift van 'directe uitstroom' naar 'meetbare stappen richting participatie'.",
      "Wij helpen gemeenten de verordening op maat te formuleren: doelgroep, aanmeldroute, rapportagefrequentie en exit-criteria in één beleidsdocument.",
    ],
    body: [
      {
        h: "Wat verandert er",
        p: "De nieuwe modelverordening geeft gemeenten meer beleidsvrijheid om ontwikkel- en participatievoorzieningen in te richten voor inwoners die nog niet direct bemiddelbaar zijn naar werk. De focus verschuift van 'directe uitstroom' naar 'meetbare stappen richting participatie'.",
      },
      {
        h: "Waar LevelLeven past",
        p: "LevelLeven is precies zo'n voorziening: een gestructureerde ontwikkelfase van twaalf weken, met start-, tussen- en eindmeting, meetbare tussenuitkomsten (zelfregie, structuur, belastbaarheid, participatie) en een duidelijke uitstroomroute naar werk of opleiding.",
      },
      {
        h: "Hoe u dit in beleid vastlegt",
        p: "Wij helpen gemeenten bij het formuleren van de verordening op maat: welke categorieën inwoners, welke aanmeldroute, welke rapportagefrequentie, welke exit-criteria. In één beleidsdocument dat door de raad verdedigbaar is.",
      },
      {
        h: "Volgende stap",
        p: "Vraag een oriënterend gesprek aan. In een uur weten we of LevelLeven past bij uw begroting, uw doelgroep en uw politieke agenda, en waar de eerste pilot start.",
      },
    ],
    bronnen: [
      "Vereniging van Nederlandse Gemeenten. (2026, 16 juli). Nieuwe Model Re-integratieverordening Participatiewet en Model Verordening maatregelen Participatiewet, IOAW en IOAZ.",
    ],
  },
];
